/**
 * Restaurant stock hero catalog for Design Lab review.
 * Keep filenames in sync with `apps/studio-shell/public/platform/defaults/heroes/restaurant/`
 * and `apps/studio-shell/src/lib/platform/defaultHeroImage.ts`.
 */
import {
  LAYOUT_COMPONENT_TYPE,
  NAV_LOGO_SURFACE,
  REGISTRY_BLOCK,
  THEME_BLOCK_PRESENTATIONS,
  THEME_ID,
  type LayoutBlock,
  type NavLogoSurface,
  type RegistryBlockKey,
  type ThemeId,
} from '@web-studio/shared-schemas';

export type HeroReviewFamily =
  | 'fine-dining'
  | 'sushi-bar'
  | 'asian-vietnam'
  | 'vietnam-shopfront'
  | 'asian-japan'
  | 'asian-china'
  | 'imbiss'
  | 'bistro'
  | 'doner';

export interface HeroStockEntry {
  id: string;
  family: HeroReviewFamily;
  surface: NavLogoSurface;
  path: string;
  label: string;
}

const DIR = '/platform/defaults/heroes/restaurant';

function entry(
  family: HeroReviewFamily,
  surface: NavLogoSurface,
  nn: string,
  label: string,
): HeroStockEntry {
  const file = `${family}-${surface}-${nn}.jpg`;
  return {
    id: file.replace(/\.jpg$/, ''),
    family,
    surface,
    path: `${DIR}/${file}`,
    label,
  };
}

const FAMILY_LABEL: Record<HeroReviewFamily, string> = {
  'fine-dining': 'Fine dining',
  'sushi-bar': 'Sushi bar',
  'asian-vietnam': 'Vietnam bowls',
  'vietnam-shopfront': 'Vietnam shopfront',
  'asian-japan': 'Japan neighborhood',
  'asian-china': 'China',
  imbiss: 'Imbiss',
  bistro: 'Bistro',
  doner: 'Döner',
};

const VARIANT_HINT: Record<string, string> = {
  '01': 'A',
  '02': 'B',
  '03': 'C',
};

const HERO_VARIANT_IDS = ['01', '02', '03'] as const;

export const HERO_REVIEW_FAMILIES = [
  'fine-dining',
  'sushi-bar',
  'asian-vietnam',
  'vietnam-shopfront',
  'asian-japan',
  'asian-china',
  'imbiss',
  'bistro',
  'doner',
] as const;

export const RESTAURANT_HERO_STOCK: readonly HeroStockEntry[] =
  HERO_REVIEW_FAMILIES.flatMap((family) =>
    ([NAV_LOGO_SURFACE.onDark, NAV_LOGO_SURFACE.onLight] as const).flatMap((surface) =>
      HERO_VARIANT_IDS.map((nn) =>
        entry(
          family,
          surface,
          nn,
          `${FAMILY_LABEL[family]} · ${surface} · ${VARIANT_HINT[nn] ?? nn}`,
        ),
      ),
    ),
  );

export type HeroReviewPresentation =
  | 'full-bleed'
  | 'editorial'
  | 'standard'
  | 'collage'
  | 'quiet-luxury';

export const HERO_REVIEW_PRESENTATIONS: ReadonlyArray<{
  id: HeroReviewPresentation;
  label: string;
  registryKey: RegistryBlockKey;
}> = [
  {
    id: 'full-bleed',
    label: 'Full Bleed',
    registryKey: REGISTRY_BLOCK.presentation.heroFullBleedImage,
  },
  {
    id: 'editorial',
    label: 'Editorial Split',
    registryKey: REGISTRY_BLOCK.presentation.heroEditorialSplit,
  },
  {
    id: 'standard',
    label: 'Standard',
    registryKey: REGISTRY_BLOCK.presentation.heroStandard,
  },
  {
    id: 'collage',
    label: 'Collage Story',
    registryKey: REGISTRY_BLOCK.presentation.heroCollageStory,
  },
  {
    id: 'quiet-luxury',
    label: 'Quiet Luxury',
    registryKey: REGISTRY_BLOCK.presentation.heroQuietLuxury,
  },
];

/** Theme that matches the photo’s tonal surface (+ family when known). */
export function suggestedThemeForHero(entry: HeroStockEntry): string {
  if (entry.family === 'sushi-bar') return THEME_ID.hinokiCounter;
  if (entry.family === 'bistro') return THEME_ID.bistroBrasserie;
  if (entry.family === 'fine-dining') return THEME_ID.michelinDark;
  if (entry.family === 'asian-vietnam') return THEME_ID.lotusSteam;
  if (entry.family === 'vietnam-shopfront') return THEME_ID.tileAwning;
  if (entry.family === 'asian-japan') return THEME_ID.lanternCourt;
  return entry.surface === NAV_LOGO_SURFACE.onDark
    ? THEME_ID.michelinDark
    : THEME_ID.bistroBrasserie;
}

/** Default hero arrangement for a studio theme (same recipe as bootstrap). */
export function suggestedPresentationForTheme(themeId: string): HeroReviewPresentation {
  const presentations = THEME_BLOCK_PRESENTATIONS[themeId as ThemeId];
  const heroKey = presentations?.[REGISTRY_BLOCK.type.hero];
  const match = HERO_REVIEW_PRESENTATIONS.find((p) => p.registryKey === heroKey);
  return match?.id ?? 'editorial';
}

export function buildHeroReviewBlock(
  entry: HeroStockEntry,
  presentation: HeroReviewPresentation,
): LayoutBlock {
  const meta = HERO_REVIEW_PRESENTATIONS.find((p) => p.id === presentation)!;
  return {
    blockId: 'hero-stock-review',
    componentType: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
    registryBlockKey: meta.registryKey,
    displayOrder: 0,
    content: {
      eyebrow: {
        de: `${entry.family} · ${entry.surface}`,
        en: `${entry.family} · ${entry.surface}`,
        vi: `${entry.family} · ${entry.surface}`,
      },
      heading: {
        de: entry.label,
        en: entry.label,
        vi: entry.label,
      },
      subheading: {
        de: 'Stock-Hero-Review — prüfe Ton, Zuschnitt und Lesbarkeit auf dem Template.',
        en: 'Stock hero review — check tone, crop, and readability on the template.',
        vi: 'Review hero stock — kiểm tra tông, crop và độ đọc trên template.',
      },
      ctaPrimary: {
        de: 'Tisch reservieren',
        en: 'Reserve a table',
        vi: 'Đặt bàn',
      },
      ctaSecondary: {
        de: 'Speisekarte',
        en: 'Menu',
        vi: 'Thực đơn',
      },
    },
    media: {
      heroImage: entry.path,
      // Collage uses secondary slots — reuse so the card isn’t empty.
      heroImageSecondary: entry.path,
      heroImageTertiary: entry.path,
    },
  };
}
